import React, { Dispatch, SetStateAction, useContext, useState, createContext } from 'react';

import {
  ElectionDescValue,
  FetchResult,
  InitiateForCodeMutation,
  MutationResult,
  OtherAuthorizedUser,
  UpdateElecDeliveryMutation,
  UpdateUserEmailMutation,
  useErrorLogger,
  useInitiateForCodeMutation,
  useUpdateElecDeliveryMutation,
  useUpdateUserEmailMutation,
  useUserVerificationQuery,
} from '@bessemercws/graphql';
import { useClientContext } from '@bessemercws/providers';

export interface UserVerificationContext {
  email: string | null;
  pendingEmail: string | null;
  isVerified: boolean;
  isDialogOpen: boolean;
  currentStep: number;
  loading: boolean;
  hasPreferencesError: boolean;
  initiateEmailChange: (email: string) => Promise<FetchResult<InitiateForCodeMutation>>;
  updateUserEmail: (code: string, email: string) => Promise<FetchResult<UpdateUserEmailMutation>>;
  setEmailVerified: Dispatch<SetStateAction<boolean>>;
  setEmailDialogOpen: Dispatch<SetStateAction<boolean>>;
  setCurrentStep: Dispatch<SetStateAction<number>>;
  successVerify?: boolean;
  onValidate: (code: string, email: string) => Promise<FetchResult<UpdateUserEmailMutation>>;
  onResendCode: (value: string) => void;
  initiateChangeEmailResult?: MutationResult<InitiateForCodeMutation>;
  updateUserEmailResult?: MutationResult<UpdateUserEmailMutation>;
  handleUpdateElecDelivery: (value: ElectionDescValue) => Promise<FetchResult<UpdateElecDeliveryMutation>>;
  otherAuthorizedUserDetails: OtherAuthorizedUser[];
}

const defaultContext: UserVerificationContext = {
  email: null,
  pendingEmail: null,
  isVerified: false,
  isDialogOpen: false,
  currentStep: 0,
  loading: false,
  hasPreferencesError: false,
  initiateEmailChange: () => new Promise(() => null),
  updateUserEmail: () => new Promise(() => null),
  setCurrentStep: () => new Promise(() => null),
  successVerify: false,
  onValidate: () => new Promise(() => null),
  onResendCode: () => new Promise(() => null),
  handleUpdateElecDelivery: () => new Promise(() => null),
  otherAuthorizedUserDetails: [],
  setEmailVerified: () => {
    // No-op function for default context
  },
  setEmailDialogOpen: () => {
    // No-op function for default context
  }
};

const VerificationContext = createContext(defaultContext);

interface UserVerificationProviderProps {
  children: React.ReactNode;
}

export const UserVerificationProvider = ({ children }: UserVerificationProviderProps) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const { isAdvisor, isAuthing } = useClientContext()
  const onError = useErrorLogger();
  const [initiateChangeEmail, initiateChangeEmailResult] = useInitiateForCodeMutation({ onError });
  const [updateUserEmail, updateUserEmailResult] = useUpdateUserEmailMutation({ onError });
  const [updateElecDelivery] = useUpdateElecDeliveryMutation({ onError });
  const { data, loading, errors } = useUserVerificationQuery({ onError, skip: isAdvisor || isAuthing });
  
  const initiateEmailChange = (newEmail: string) => {
    return initiateChangeEmail({
      variables: { input: newEmail },
      fetchPolicy: 'network-only',
    });
  };

  const updateEmail = (code: string, newEmail: string) => {
    return updateUserEmail({
      variables: { code, input: newEmail },
      update(cache, { data }) {
        const updatedUser = data?.updateEmail?.userId;
        if (updatedUser) {
          cache.modify({
            id: `UserProfile:${data?.updateEmail?.userId}`,
            fields: {
              emailAddress() {
                return data?.updateEmail?.email;
              },
              pendingEmailAddress() {
                return null; // once email is updated there is no more pending email
              },
            },
          });
        }
      },
    });
  };

  const handleValidateCode = (code: string, validateEmail: string) => {
    return updateEmail(code, validateEmail);
  };

  const handleResendCode = (value: string) => {
    return initiateEmailChange(value);
  };

  const handleUpdateElecDelivery = (electionDesc: ElectionDescValue) => {
    return updateElecDelivery({
      variables: { input: { electionDesc } },
      update(cache, { data }) {
        cache.modify({
          id: `UserProfile:${data?.updateElectronicDelivery.userId}`,
          fields: {
            electronicDelivery(currUserElecDelivery) {
              return data?.updateElectronicDelivery.electionDesc ?? currUserElecDelivery;
            },
            verifiedDeliveryOption(currentVerifiedDeliveryOption) {
              return data?.updateElectronicDelivery.electionDesc ?? currentVerifiedDeliveryOption;
            },
          },
        });
      },
    });
  };

  const userProfile = data?.viewer?.preferences?.userProfile;
  const hasPreferencesError = !!errors?.length || (!loading && !data?.viewer?.preferences?.userProfile);
  
  return (
    <VerificationContext.Provider
      value={{
        email: userProfile?.emailAddress ?? null,
        pendingEmail: userProfile?.pendingEmailAddress ?? null,
        isVerified: !!userProfile?.electronicDelivery,
        isDialogOpen: false,
        currentStep,
        loading,
        hasPreferencesError,
        initiateEmailChange,
        updateUserEmail: updateEmail,
        setEmailVerified: defaultContext.setEmailVerified,
        setEmailDialogOpen: defaultContext.setEmailDialogOpen,
        setCurrentStep,
        onValidate: handleValidateCode,
        onResendCode: handleResendCode,
        initiateChangeEmailResult,
        updateUserEmailResult,
        handleUpdateElecDelivery,
        otherAuthorizedUserDetails: data?.viewer?.preferences?.userProfile?.otherAuthorizedUserDetails ?? [],
      }}
    >
      {children}
    </VerificationContext.Provider>
  );
};

const useUserVerificationContext = () => {
  const UserVerificationProvider = useContext(VerificationContext);

  if (UserVerificationProvider === undefined) {
    throw new Error('must be inside a UserVerificationProvider');
  }

  return UserVerificationProvider;
};

export { VerificationContext, useUserVerificationContext };
